import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  FileText,
  Loader2,
  MessageSquare,
  Scale,
  Wrench,
} from 'lucide-react';
import {
  fetchIncoherences,
  fetchIncoherencesStats,
  fetchIncoherencesParJuridiction,
  fetchIncoherenceDetail,
  updateIncoherenceStatut,
  Incoherence,
  IncoherencesStats,
  JuridictionStats,
  IncoherencesFilters,
} from '../store/incoherences/service';
import './ControleQualite.css';

/* ────────────────────────── Types locaux ────────────────────────── */

type EtatPoint = 'a_examiner' | 'confirmee' | 'ignoree' | 'corrigee';
type Gravite = 'majeure' | 'a_verifier' | 'information';
type FiltreType = EtatPoint | 'tous' | 'divergences';

/* ────────────────────────── Labels ────────────────────────── */

const LABELS_ETAT: Record<EtatPoint, string> = {
  a_examiner: 'À examiner',
  confirmee: 'Confirmée',
  ignoree: 'Ignorée',
  corrigee: 'Corrigée',
};

const LABELS_GRAVITE: Record<Gravite, string> = {
  majeure: 'Majeure',
  a_verifier: 'À vérifier',
  information: 'Information',
};

const ONGLETS: { cle: FiltreType; label: string }[] = [
  { cle: 'a_examiner', label: 'À examiner' },
  { cle: 'divergences', label: 'Divergences détectées' },
  { cle: 'confirmee', label: 'Confirmées' },
  { cle: 'ignoree', label: 'Ignorées' },
  { cle: 'corrigee', label: 'Corrigées' },
  { cle: 'tous', label: 'Tous' },
];

const PAGE_SIZE = 20;

/* ────────────────────────── Helpers ────────────────────────── */

/** Construit les query params serveur à partir des filtres UI */
function buildParams(
  filtre: FiltreType,
  matiere: string,
  dateDebut: string,
  dateFin: string,
  offset: number,
): IncoherencesFilters {
  const params: IncoherencesFilters = { limit: PAGE_SIZE, offset };
  if (filtre === 'divergences') {
    // Divergences = majeure + a_verifier (pas information)
    // L'API ne supporte pas "not information", on ne filtre pas par statut ici
    // mais on exclut la gravite information côté API si possible
    // Sinon on récupère tout et on filtre côté client
  } else if (filtre !== 'tous') {
    params.statut = filtre;
  }
  if (matiere) params.matiere = matiere;
  if (dateDebut) params.date_debut = dateDebut;
  if (dateFin) params.date_fin = dateFin;
  return params;
}

/* ────────────────────────── Composant principal ────────────────────────── */

const ControleQualite: React.FC = () => {
  /* ── Filtres ── */
  const [filtre, setFiltre] = useState<FiltreType>('a_examiner');
  const [matiere, setMatiere] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [offset, setOffset] = useState(0);

  /* ── Données API ── */
  const [incoherences, setIncoherences] = useState<Incoherence[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<IncoherencesStats | null>(null);
  const [juridictions, setJuridictions] = useState<JuridictionStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  /* ── Détail ── */
  const [detailIncoherence, setDetailIncoherence] = useState<Incoherence | null>(null);
  const [saisies, setSaisies] = useState<Record<string, string>>({});

  /* ── Matières dynamiques (extraites des résultats) ── */
  const [matieres, setMatieres] = useState<string[]>([]);

  /* ── Chargement des données ── */
  const chargerDonnees = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(filtre, matiere, dateDebut, dateFin, offset);

      // Appels parallèles : liste + stats + juridictions
      const [listRes, statsRes, juriRes] = await Promise.all([
        fetchIncoherences(params),
        fetchIncoherencesStats(params),
        fetchIncoherencesParJuridiction(params),
      ]);

      let results = listRes.data.results;

      // Filtre client pour "divergences" (exclut gravite=information)
      if (filtre === 'divergences') {
        results = results.filter(inc => inc.gravite !== 'information');
      }

      setIncoherences(results);
      setTotalCount(listRes.data.count);
      setStats(statsRes.data);
      setJuridictions(juriRes.data);

      // Extraire les matières uniques pour le select
      const matieresSet = new Set<string>();
      results.forEach(inc => {
        if (inc.decision.matiere) matieresSet.add(inc.decision.matiere);
      });
      setMatieres(prev => {
        const merged = new Set(prev.concat(Array.from(matieresSet)));
        return Array.from(merged).sort();
      });
    } catch (err: any) {
      if (err.response?.status !== 401) {
        toast.error('Erreur lors du chargement des contrôles qualité');
      }
    } finally {
      setLoading(false);
    }
  }, [filtre, matiere, dateDebut, dateFin, offset]);

  useEffect(() => {
    chargerDonnees();
  }, [chargerDonnees]);

  // Reset offset quand les filtres changent
  useEffect(() => {
    setOffset(0);
  }, [filtre, matiere, dateDebut, dateFin]);

  /* ── Actions sur un point ── */
  const majEtat = async (incoherenceId: string, statut: 'confirmee' | 'ignoree' | 'corrigee', commentaire?: string) => {
    setLoadingAction(incoherenceId);
    try {
      const res = await updateIncoherenceStatut(incoherenceId, {
        statut,
        commentaire: commentaire || '',
      });

      // Mettre à jour l'incohérence dans la liste locale
      setIncoherences(prev =>
        prev.map(inc => inc.id === incoherenceId ? res.data : inc),
      );

      // Mettre à jour le détail si ouvert
      if (detailIncoherence?.id === incoherenceId) {
        setDetailIncoherence(res.data);
      }

      // Recharger les stats
      const params = buildParams(filtre, matiere, dateDebut, dateFin, offset);
      const [statsRes, juriRes] = await Promise.all([
        fetchIncoherencesStats(params),
        fetchIncoherencesParJuridiction(params),
      ]);
      setStats(statsRes.data);
      setJuridictions(juriRes.data);

      toast.success(`Point marqué comme « ${LABELS_ETAT[statut]} »`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoadingAction(null);
    }
  };

  const ajouterCommentaire = (incoherenceId: string) => {
    const texte = (saisies[incoherenceId] || '').trim();
    if (!texte) return;
    // Le commentaire est envoyé avec le prochain changement de statut
    // Pour l'instant on le stocke localement
    setSaisies(prev => ({ ...prev, [incoherenceId]: '' }));
    toast.info('Commentaire enregistré — il sera envoyé lors du prochain changement de statut.');
  };

  const ouvrirDetail = async (incoherenceId: string) => {
    try {
      const res = await fetchIncoherenceDetail(incoherenceId);
      setDetailIncoherence(res.data);
    } catch {
      toast.error('Impossible de charger le détail');
    }
  };

  /* ── Pagination ── */
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  /* ── Regrouper les incohérences par décision ── */
  const parDecision = incoherences.reduce<
    Record<string, { decision: Incoherence['decision']; incoherences: Incoherence[] }>
  >((acc, inc) => {
    const key = inc.decision.id;
    if (!acc[key]) {
      acc[key] = { decision: inc.decision, incoherences: [] };
    }
    acc[key].incoherences.push(inc);
    return acc;
  }, {});
  const groupes = Object.values(parDecision);

  /* ────────────────────────── Vue détail ────────────────────────── */

  if (detailIncoherence) {
    const d = detailIncoherence.decision;
    const inc = detailIncoherence;
    return (
      <main className="cq-page cq-detail-page">
        <button className="cq-retour" onClick={() => setDetailIncoherence(null)}>
          <ArrowLeft size={19} /> Retour aux contrôles qualité
        </button>

        <section className="cq-detail-hero">
          <h2>{d.juridiction} : N° {d.numero}</h2>
          <p>
            {d.matiere && `Matière : ${d.matiere} · `}
            {d.numero_rg && `RG n° ${d.numero_rg} · `}
            {new Date(d.date_decision).toLocaleDateString('fr-FR')}
          </p>
          <div className="cq-detail-badges">
            <span className={`cq-badge-etat cq-badge-${inc.statut}`}>
              {LABELS_ETAT[inc.statut]}
            </span>
            <span className="cq-alerte-pill">
              <AlertTriangle size={14} /> Gravité : {LABELS_GRAVITE[inc.gravite]}
            </span>
          </div>
        </section>

        {/* ── Incohérence ── */}
        <h3 className="cq-section-titre">Incohérence</h3>
        <section className="cq-info-grid">
          <InfoCarte titre={inc.titre}>
            <div className="cq-meta-grid">
              <span><b>Type</b>{inc.type}</span>
              <span><b>Gravité</b>{LABELS_GRAVITE[inc.gravite]}</span>
              <span><b>Statut</b>{LABELS_ETAT[inc.statut]}</span>
              <span><b>Créée le</b>{new Date(inc.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              {inc.decidee_par && <span><b>Décidée par</b>{inc.decidee_par}</span>}
              {inc.decidee_le && <span><b>Décidée le</b>{new Date(inc.decidee_le).toLocaleDateString('fr-FR')}</span>}
            </div>

            <p className="cq-detail-description"><b>Description</b><br />{inc.description}</p>

            {inc.verbatim && (
              <div className="cq-formulation">
                <strong>Formulation dans la décision :</strong>
                <em>{inc.verbatim}</em>
              </div>
            )}

            {inc.commentaire && (
              <p className="cq-detail-description"><b>Commentaire</b><br />{inc.commentaire}</p>
            )}
          </InfoCarte>
        </section>

        {/* ── Valeurs ── */}
        {inc.valeurs && inc.valeurs.length > 0 && (
          <>
            <h3 className="cq-section-titre">Valeurs ({inc.valeurs.length})</h3>
            <section className="cq-info-grid">
              {inc.valeurs.map((v, i) => (
                <InfoCarte key={i} titre={`Valeur ${i + 1}`}>
                  <div className="cq-meta-grid">
                    <span><b>Valeur</b>{v.valeur}</span>
                    <span><b>Emplacement</b>{v.emplacement}</span>
                    <span><b>Page</b>{v.page}</span>
                  </div>
                </InfoCarte>
              ))}
            </section>
          </>
        )}

        {/* ── Objets liés ── */}
        {inc.objets_lies && inc.objets_lies.length > 0 && (
          <>
            <h3 className="cq-section-titre">Objets liés ({inc.objets_lies.length})</h3>
            <section className="cq-info-grid">
              <InfoCarte titre="Références">
                <div className="cq-objets-lies">
                  {inc.objets_lies.map((obj, i) => (
                    <span key={i} className="cq-objet-lie">
                      <b>{obj.type}</b> {obj.ref}
                    </span>
                  ))}
                </div>
              </InfoCarte>
            </section>
          </>
        )}

        {/* ── Décision associée ── */}
        <h3 className="cq-section-titre">Décision associée</h3>
        <section className="cq-info-grid">
          <InfoCarte titre="Décision">
            <div className="cq-meta-grid">
              <span><b>Juridiction</b>{d.juridiction || '–'}</span>
              <span><b>Numéro</b>{d.numero || '–'}</span>
              <span><b>RG</b>{d.numero_rg || '–'}</span>
              <span><b>Matière</b>{d.matiere || '–'}</span>
              <span><b>Date</b>{new Date(d.date_decision).toLocaleDateString('fr-FR')}</span>
              <span><b>Ville</b>{d.ville || '–'}</span>
              <span><b>Statut décision</b>{d.statut}</span>
            </div>
          </InfoCarte>
        </section>

        {/* ── Actions ── */}
        <h3 className="cq-section-titre">Actions</h3>
        <section className="cq-detail-points">
          <CartePoint
            incoherence={detailIncoherence}
            saisie={saisies[detailIncoherence.id] || ''}
            onSaisie={val => setSaisies(prev => ({ ...prev, [detailIncoherence.id]: val }))}
            onAjouterCommentaire={() => ajouterCommentaire(detailIncoherence.id)}
            onMajEtat={(statut) => majEtat(detailIncoherence.id, statut)}
            loadingAction={loadingAction === detailIncoherence.id}
          />
        </section>
      </main>
    );
  }

  /* ────────────────────────── Vue liste ────────────────────────── */

  return (
    <main className="cq-page">
      <section className="cq-heading">
        <h1>Contrôles qualité</h1>
        <p>
          Les points ci-dessous signalent des divergences ou incohérences potentielles relevées dans
          les documents. Ils ne préjugent en rien du fond de la décision : la vérification finale
          appartient au greffe.
        </p>
      </section>

      {/* KPI */}
      <section className="cq-kpis">
        <CarteKpi label="Points à examiner" valeur={stats?.a_examiner ?? 0} loading={loading} />
        <CarteKpi label="Divergences confirmées" valeur={stats?.confirmees ?? 0} loading={loading} />
        <CarteKpi label="Points corrigés" valeur={stats?.corrigees ?? 0} loading={loading} />
        <CarteKpi label="Décisions concernées" valeur={stats?.decisions_concernees ?? 0} loading={loading} />
      </section>

      {/* Par juridiction */}
      {juridictions.length > 0 && (
        <section className="cq-juridiction-card">
          <h3 className="cq-juridiction-titre">
            <Scale size={18} /> Par juridiction
          </h3>
          <div className="cq-juridiction-table">
            <div className="cq-juridiction-head">
              <span>Juridiction</span><span>Points</span><span>Majeurs</span><span>Décisions</span>
            </div>
            {juridictions.map(m => (
              <div className="cq-juridiction-row" key={m.juridiction}>
                <span>{m.juridiction}</span>
                <span>
                  <span className="cq-juridiction-val cq-juridiction-val--points">{m.points}</span>
                </span>
                <span>
                  <span className={`cq-juridiction-val ${m.majeurs > 0 ? 'cq-juridiction-val--majeur' : 'cq-juridiction-val--zero'}`}>
                    {m.majeurs}
                  </span>
                </span>
                <span>
                  <span className="cq-juridiction-val">{m.decisions}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filtres */}
      <section className="cq-filtres">
        <div className="cq-onglets">
          {ONGLETS.map(o => (
            <button
              key={o.cle}
              className={`cq-onglet ${filtre === o.cle ? 'cq-onglet-actif' : ''}`}
              onClick={() => setFiltre(o.cle)}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="cq-champs">
          <label className="cq-date-label">
            Du
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
          </label>
          <label className="cq-date-label">
            Au
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
          </label>
          {(dateDebut || dateFin) && (
            <button className="cq-onglet" onClick={() => { setDateDebut(''); setDateFin(''); }}>
              Effacer la période
            </button>
          )}
          <select value={matiere} onChange={e => setMatiere(e.target.value)}>
            <option value="">Toutes les matières</option>
            {matieres.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </section>

      {/* Liste */}
      <section className="cq-decisions">
        {loading && (
          <div className="cq-loading">
            <Loader2 size={28} className="cq-spinner" />
            <span>Chargement…</span>
          </div>
        )}

        {!loading && groupes.length === 0 && (
          <div className="cq-vide">
            <FileText size={28} />
            <strong>Aucun point dans ce filtre.</strong>
          </div>
        )}

        {!loading && groupes.map(({ decision: dec, incoherences: incs }) => {
          const majeures = incs.filter(inc => inc.gravite === 'majeure').length;
          return (
            <article className="cq-decision" key={dec.id}>
              <header className="cq-decision-header">
                <div>
                  <h2>{dec.juridiction} : N° {dec.numero}</h2>
                  <p className="cq-decision-meta">
                    Matière : {dec.matiere} · RG n° {dec.numero_rg} · {new Date(dec.date_decision).toLocaleDateString('fr-FR')}
                    {majeures > 0 && ` · ${majeures} point(s) majeur(s)`}
                  </p>
                </div>
              </header>

              <div className="cq-decision-body">
                <ul className="cq-point-liste">
                  {incs.map(inc => (
                    <CartePoint
                      key={inc.id}
                      incoherence={inc}
                      saisie={saisies[inc.id] || ''}
                      onSaisie={val => setSaisies(prev => ({ ...prev, [inc.id]: val }))}
                      onAjouterCommentaire={() => ajouterCommentaire(inc.id)}
                      onMajEtat={(statut) => majEtat(inc.id, statut)}
                      onVoirDetail={() => ouvrirDetail(inc.id)}
                      loadingAction={loadingAction === inc.id}
                    />
                  ))}
                </ul>
              </div>
            </article>
          );
        })}
      </section>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="cq-pagination">
          <button
            className="cq-pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <span className="cq-pagination-info">
            Page {currentPage} sur {totalPages} · {totalCount} résultat(s)
          </span>
          <button
            className="cq-pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Suivant <ChevronRight size={16} />
          </button>
        </div>
      )}

      <p className="cq-pied">
        {totalCount} incohérence(s) suivie(s) par le greffe. Les traitements automatiques
        n'apportent qu'une aide à la relecture : aucune mention ne vaut appréciation juridique.
      </p>
    </main>
  );
};

/* ────────────────────────── Sous-composants ────────────────────────── */

const CarteKpi: React.FC<{ label: string; valeur: number; loading?: boolean }> = ({ label, valeur, loading }) => (
  <div className="cq-kpi">
    <span className="cq-kpi-label">{label}</span>
    <strong className="cq-kpi-valeur">{loading ? '–' : valeur}</strong>
  </div>
);

interface CartePointProps {
  incoherence: Incoherence;
  saisie: string;
  onSaisie: (val: string) => void;
  onAjouterCommentaire: () => void;
  onMajEtat: (statut: 'confirmee' | 'ignoree' | 'corrigee') => void;
  onVoirDetail?: () => void;
  loadingAction?: boolean;
}

const CartePoint: React.FC<CartePointProps> = ({
  incoherence: inc,
  saisie,
  onSaisie,
  onAjouterCommentaire,
  onMajEtat,
  onVoirDetail,
  loadingAction,
}) => {
  const gravite = inc.gravite as Gravite;
  const statut = inc.statut as EtatPoint;
  const premierValeur = inc.valeurs?.[0];

  return (
    <li className={`cq-point cq-point-${gravite}`}>
      <div className="cq-point-entete">
        <div>
          <h4>{inc.titre}</h4>
          <p className="cq-gravite">
            <AlertTriangle size={18} /> Gravité : {LABELS_GRAVITE[gravite]}
          </p>
        </div>
        <span className={`cq-badge-etat cq-badge-${statut}`}>{LABELS_ETAT[statut]}</span>
      </div>

      {premierValeur && (
        <>
          <p><strong>Valeur :</strong> {premierValeur.valeur}</p>
          <p><strong>Emplacement :</strong> {premierValeur.emplacement} · <strong>Page :</strong> {premierValeur.page}</p>
        </>
      )}
      <p><strong>Description :</strong> {inc.description}</p>

      {inc.verbatim && (
        <div className="cq-formulation">
          <strong>Formulation dans la décision :</strong>
          <em>{inc.verbatim}</em>
        </div>
      )}

      {inc.commentaire && (
        <ul className="cq-commentaires">
          <li className="cq-commentaire-sauvegarde">
            <MessageSquare size={14} /> {inc.commentaire}
          </li>
        </ul>
      )}

      <div className="cq-point-boutons">
        <button onClick={() => onMajEtat('confirmee')} disabled={loadingAction}>
          {loadingAction ? <Loader2 size={16} className="cq-spinner" /> : <Check size={16} />} Confirmer la divergence
        </button>
        <button onClick={() => onMajEtat('ignoree')} disabled={loadingAction}>
          {loadingAction ? <Loader2 size={16} className="cq-spinner" /> : <EyeOff size={16} />} Ignorer
        </button>
        <button onClick={() => onMajEtat('corrigee')} disabled={loadingAction}>
          {loadingAction ? <Loader2 size={16} className="cq-spinner" /> : <Wrench size={16} />} Marquer comme corrigé
        </button>
      </div>

      <form className="cq-commentaire-form" onSubmit={e => { e.preventDefault(); onAjouterCommentaire(); }}>
        <input
          value={saisie}
          onChange={e => onSaisie(e.target.value)}
          placeholder="Ajouter un commentaire du greffe…"
        />
        <button type="submit"><MessageSquare size={17} /> Commenter</button>
      </form>

      {onVoirDetail && (
        <button className="cq-btn-primary cq-btn-voir-detail" onClick={onVoirDetail}>
          Voir le détail
        </button>
      )}
    </li>
  );
};

const InfoCarte: React.FC<{ titre: string; children: React.ReactNode }> = ({ titre, children }) => (
  <section className="cq-info-carte">
    <h3>{titre}</h3>
    {children}
  </section>
);

export default ControleQualite;
